import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { StrategyDspAudienceType } from "@front/m19-api-client";
import { AudienceId, DspStrategyService } from "@front/m19-services";
import { IButtonDirective, MODAL_DATA, ModalRef } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DspAudiencesSelectorComponent } from "@m19-board/dsp/dsp-audiences-selector/dsp-audiences-selector.component";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { strategyDspAudiencesValidator } from "@m19-board/utils/validators/dspAudiencesValidators";

export interface DspAudienceModalData {
  audienceType: StrategyDspAudienceType;
  selectedAudiences: AudienceId[][];
}

export interface DspAudienceModalResult {
  addedAudiences: AudienceId[][];
  deletedAudiences: AudienceId[][];
}

@Component({
  selector: "app-dsp-audience-modal",
  template: `
    <div class="flex h-full flex-col" *transloco="let t">
      <div class="-mx-4 min-h-0 w-full flex-1 overflow-auto">
        <app-dsp-audiences-selector
          [selectedAudiences]="newAudiences()"
          (selectedAudiencesChange)="setNewAudiences($event)"
          [audienceType]="modalData.audienceType"
          [style]="'outlined'"
        >
        </app-dsp-audiences-selector>
      </div>
      <div class="flex w-full shrink-0 items-center border-t bg-white p-2">
        <div class="text-sm text-gray-500">
          {{ warningMessage }}
        </div>
        <div class="flex flex-1 justify-end gap-2">
          <button iButton variant="secondary" size="md" (click)="close()">{{ t("common.close") }}</button>
          <button iButton size="md" [disabled]="!canSaveAudiences() || form.invalid" (click)="save()">
            {{ t("common.save") }}
          </button>
        </div>
      </div>
    </div>
  `,
  imports: [IButtonDirective, TranslocoDirective, DspAudiencesSelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAudienceModalComponent implements OnInit {
  modalData = inject<DspAudienceModalData>(MODAL_DATA);
  modalRef = inject(ModalRef);
  translocoService = inject(TranslocoService);

  readonly newAudiences = signal([...this.modalData.selectedAudiences.map((audienceGroup) => [...audienceGroup])]);
  readonly warningMessage =
    this.modalData.audienceType === StrategyDspAudienceType.BLACKLIST
      ? this.translocoService.translate("dsp-strategy-page.max_audience_blacklist", {
          max: DspStrategyService.MAX_AUDIENCE_PER_GROUP,
        })
      : this.translocoService.translate("dsp-strategy-page.max_audience_warning", {
          maxGroupPerStrategy: DspStrategyService.MAX_AUDIENCE_GROUP_PER_STRATEGY,
          maxAudiencePerGroup: DspStrategyService.MAX_AUDIENCE_PER_GROUP,
        });

  readonly form = new FormGroup({
    audiences: new FormControl<AudienceId[][]>([[]], {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    }),
  });

  readonly addedAudiences = computed(() =>
    this.getChangedAudiences(this.modalData.selectedAudiences, this.newAudiences()),
  );
  readonly deletedAudiences = computed(() =>
    this.getChangedAudiences(this.newAudiences(), this.modalData.selectedAudiences),
  );

  readonly hasChangedAudience = computed(() => {
    return (
      this.addedAudiences().some((audience) => audience.length > 0) ||
      this.deletedAudiences().some((audience) => audience.length > 0)
    );
  });

  readonly canSaveAudiences = computed(() => {
    const hasSelectedAudiences = this.newAudiences().length > 0;
    return this.hasChangedAudience() && hasSelectedAudiences;
  });

  ngOnInit() {
    if (this.modalData.audienceType === StrategyDspAudienceType.WHITELIST) {
      this.form.controls.audiences.addValidators(strategyDspAudiencesValidator());
    }
  }

  setNewAudiences(selectedAudiences: AudienceId[][]) {
    this.form.controls.audiences.setValue([...selectedAudiences]);
    this.newAudiences.set([...selectedAudiences]);
  }

  save() {
    this.modalRef.close(<DspAudienceModalResult>{
      addedAudiences: this.addedAudiences(),
      deletedAudiences: this.deletedAudiences(),
    });
  }

  close() {
    this.modalRef.close();
  }

  private getChangedAudiences(existingAudiences: AudienceId[][], newAudiences: AudienceId[][]): AudienceId[][] {
    const existingAudiencesChanged = existingAudiences.map((audienceGroup, index) =>
      this.getChangedAudienceGroups(audienceGroup, newAudiences[index] ?? []),
    );

    if (newAudiences.length > existingAudiences.length) {
      existingAudiencesChanged.push(...newAudiences.slice(existingAudiences.length));
    }

    return existingAudiencesChanged;
  }

  private getChangedAudienceGroups(existingAudienceGroup: AudienceId[], newAudienceGroup: AudienceId[]) {
    const existingAudienceIds = new Set(existingAudienceGroup.map((audience) => audience.audienceId));
    return newAudienceGroup.filter((audience) => !existingAudienceIds.has(audience.audienceId));
  }
}
